import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Settings, 
  Users, RotateCcw, Percent, Star, BarChart3, Award,
  Shield, Bell, ExternalLink, ChevronDown, Paintbrush
} from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function AdminLayout({ children }) {
  const location = useLocation();
  
  const mainNavItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    { path: '/admin/products', label: 'Products', icon: Package },
    { path: '/admin/customers', label: 'Customers', icon: Users },
    { path: '/admin/site-builder', label: 'Site Builder', icon: Paintbrush },
  ];

  const moreNavItems = [
    { path: '/admin/returns', label: 'Returns', icon: RotateCcw },
    { path: '/admin/discounts', label: 'Discounts', icon: Percent },
    { path: '/admin/reviews', label: 'Reviews', icon: Star },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/loyalty', label: 'Loyalty', icon: Award },
    { path: '/admin/settings', label: 'Legacy Settings', icon: Settings },
    { path: '/admin/security', label: 'Security', icon: Shield },
    { path: '/admin/notifications', label: 'Notifications', icon: Bell },
  ];

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const isMoreActive = moreNavItems.some(item => isActive(item.path));

  return (
    <div className="min-h-screen bg-stone-50 pt-20">
      {/* Top Admin Bar */}
      <div className="fixed top-20 left-0 right-0 bg-stone-900 text-white z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12">
            {/* Left - Navigation */}
            <nav className="flex items-center space-x-1">
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      isActive(item.path)
                        ? 'bg-white/20 text-white'
                        : 'text-stone-300 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{item.label}</span>
                  </Link>
                );
              })}
              
              {/* More dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  isMoreActive
                    ? 'bg-white/20 text-white'
                    : 'text-stone-300 hover:text-white hover:bg-white/10'
                }`}>
                  More
                  <ChevronDown className="h-3 w-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {moreNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.path} asChild>
                        <Link 
                          to={item.path}
                          className={`flex items-center gap-2 ${
                            isActive(item.path) ? 'bg-stone-100' : ''
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Right - Quick Links */}
            <div className="flex items-center gap-2">
              <Link
                to="/admin/notifications"
                className="relative p-2 text-stone-300 hover:text-white rounded transition-colors"
              >
                <Bell className="h-4 w-4" />
              </Link>
              <Link
                to="/"
                target="_blank"
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-stone-300 hover:text-white rounded border border-stone-700 hover:border-stone-500 transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                View Site
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-12">
        {children}
      </div>
    </div>
  );
}
